import { isString, get, omitBy, isNil } from 'lodash-es';
import moment from 'moment';
import type { CoreTypes } from '../../core';
import type { Duty } from '../types/duties';
import { getDivision, getDivisionId, hasDivisionId, toReference, type DivisionScope } from './common';
import toPerson from './persons';

type Expand = ('referenceNames' | 'person')[];

/**
 * @deprecated This is only used for SCS. can be removed when scs use v2
 */
export default (
  teachers:   Extract<CoreTypes.Mixed.Teacher, { id: string }>[],
  domain?:    DivisionScope,
  expand?:    Expand
): Duty[] => {
  if (!Array.isArray(teachers))
    throw new Error('(SS12000::To::Duty) Teacher input is not of array type', { cause: 'invalid_input' });

  if (!teachers.every((teacher) => isString(teacher) || isString(get(teacher, 'id'))))
    throw new Error('(SS12000::To::Duty) Some teachers are missing "id" values', { cause: 'invalid_input' });

  if (!domain && !teachers.every((teacher) => !isString(teacher) && hasDivisionId(teacher)))
    throw new Error('(SS12000::To::Duty) Some top layer teachers are missing division values', { cause: 'invalid_input' });

  return teachers.map((teacher) => {
    const division = getDivision(teacher, domain);
    if (!division || isString(division) || !('id' in division && division.id))
      throw new Error('(SS12000::To::Duty) Some teachers members are missing division values', { cause: 'invalid_input' });
    if (!('start' in division && division.start) || !('end' in division && division.end))
      throw new Error('(SS12000::To::Duty) Some teachers members are missing division start or end values', { cause: 'invalid_input' });

    const person = (!isString(teacher) && teacher.person && !isString(teacher.person)) ?
      teacher.person as Extract<CoreTypes.Mixed.Person, { id: string }>
      : undefined;

    return {
      id:        `${ getDivisionId(teacher, domain) }.${ isString(teacher) ? teacher : teacher.id }`,
      dutyRole:  'Lärare' as const,
      startDate: moment(division.start).toISOString(),
      endDate:   moment(division.end).toISOString(),
      ...typeof teacher != 'string' && {
        meta: {
          created:  moment(teacher.createdAt).toISOString(),
          modified: moment(teacher.updatedAt).toISOString()
        },
        ...omitBy({
          foreignId:   teacher.ids,
          displayName: teacher.displayName,
        }, isNil),
        ...person && {
          person: {
            // @ts-ignore
            ...toReference([person], division)[0],
            ...expand?.includes('referenceNames') && {
              displayName: `${ person.firstName ?? '' } ${ person.lastName ?? '' }`
            }
          }
        },
        ...expand?.some(x => ['person'].includes(x)) && {
          _embedded: {
            ...(expand.includes('person') && person) && {
              person: toPerson([person], division as Extract<CoreTypes.Deep.Division, { id: string }>)[0]
            }
          }
        }
      }
    };
  });
};